/**
 * Job Service - Handles all job-related API calls
 * Location: src/services/jobService.js
 *
 * Uses apiClient to automatically include JWT authentication
 */

import apiClient from './apiClient';

const jobService = {
  /**
   * Create a new job posting
   * @param {object} jobData - Job information
   * @returns {Promise} Created job data
   */
  createJob: async (jobData) => {
    try {
      console.log('[jobService] 🎯 Creating new job posting...');
      console.log('[jobService] Job data:', jobData);

      const response = await apiClient.post('/external/jobs', jobData);

      console.log('[jobService] ✅ Job created successfully!');
      console.log('[jobService] Response:', response);

      return response;
    } catch (error) {
      console.error('[jobService] ❌ Failed to create job:', error.message);
      throw error;
    }
  },

  /**
   * Get all jobs
   * @returns {Promise} Array of jobs
   */
  getJobs: async () => {
    try {
      console.log('[jobService] 📋 Fetching all jobs...');

      const response = await apiClient.get('/external/jobs');

      console.log('[jobService] ✅ Jobs fetched successfully!');
      console.log('[jobService] Jobs:', response);

      return response;
    } catch (error) {
      console.error('[jobService] ❌ Failed to fetch jobs:', error.message);
      throw error;
    }
  },

  /**
   * Get jobs created by the current user
   * @returns {Promise} Array of user's jobs with candidate counts
   */
  getMyJobs: async () => {
    try {
      console.log('[jobService] 📋 Fetching my jobs...');

      const response = await apiClient.get('/external/my-jobs');

      console.log('[jobService] ✅ My jobs fetched successfully!');
      console.log('[jobService] Jobs:', response);

      return response;
    } catch (error) {
      console.error('[jobService] ❌ Failed to fetch my jobs:', error.message);
      throw error;
    }
  },

  /**
   * Get job by ID
   * @param {string|number} jobId - The job ID
   * @returns {Promise} Job data
   */
  getJobById: async (jobId) => {
    try {
      console.log(`[jobService] 🔍 Fetching job with ID: ${jobId}`);

      const response = await apiClient.get(`/external/jobs/${jobId}`);

      console.log(`[jobService] ✅ Job ${jobId} fetched successfully!`);

      return response;
    } catch (error) {
      console.error(`[jobService] ❌ Failed to fetch job ${jobId}:`, error.message);
      throw error;
    }
  },

  /**
   * Update an existing job
   * @param {string|number} jobId - The job ID
   * @param {object} jobData - Updated job information
   * @returns {Promise} Updated job data
   */
  updateJob: async (jobId, jobData) => {
    try {
      console.log(`[jobService] ✏️ Updating job ${jobId}...`);
      console.log('[jobService] Updated data:', jobData);

      const response = await apiClient.put(`/external/jobs/${jobId}`, jobData);

      console.log(`[jobService] ✅ Job ${jobId} updated successfully!`);

      return response;
    } catch (error) {
      console.error(`[jobService] ❌ Failed to update job ${jobId}:`, error.message);
      throw error;
    }
  },

  /**
   * Delete a job
   * @param {string|number} jobId - The job ID
   * @returns {Promise} Response data
   */
  deleteJob: async (jobId) => {
    try {
      console.log(`[jobService] 🗑️ Deleting job ${jobId}...`);

      const response = await apiClient.delete(`/external/jobs/${jobId}`);

      console.log(`[jobService] ✅ Job ${jobId} deleted successfully!`);

      return response;
    } catch (error) {
      console.error(`[jobService] ❌ Failed to delete job ${jobId}:`, error.message);
      throw error;
    }
  },

  /**
   * Partial update of a job (PATCH)
   * Useful for updating specific fields without providing the entire job object
   * @param {string|number} jobId - The job ID
   * @param {object} partialData - Partial job data to update
   * @returns {Promise} Updated job data
   */
  partialUpdateJob: async (jobId, partialData) => {
    try {
      console.log(`[jobService] 🔧 Partially updating job ${jobId}...`);
      console.log('[jobService] Partial data:', partialData);

      const response = await apiClient.patch(`/external/jobs/${jobId}`, partialData);

      console.log(`[jobService] ✅ Job ${jobId} partially updated successfully!`);

      return response;
    } catch (error) {
      console.error(`[jobService] ❌ Failed to partially update job ${jobId}:`, error.message);
      throw error;
    }
  },

  /**
   * Search jobs by criteria
   * @param {object} criteria - Search criteria (jobTitle, company, location, etc.)
   * @returns {Promise} Array of matching jobs
   */
  searchJobs: async (criteria) => {
    try {
      console.log('[jobService] 🔎 Searching jobs with criteria:', criteria);

      // Convert criteria object to URL query parameters
      const queryString = new URLSearchParams(criteria).toString();
      const response = await apiClient.get(`/external/jobs/search?${queryString}`);

      console.log('[jobService] ✅ Jobs search successful!');

      return response;
    } catch (error) {
      console.error('[jobService] ❌ Failed to search jobs:', error.message);
      throw error;
    }
  },

  /**
   * Get job statistics
   * @returns {Promise} Job statistics data
   */
  getJobStatistics: async () => {
    try {
      console.log('[jobService] 📊 Fetching job statistics...');

      const response = await apiClient.get('/external/jobs/statistics');

      console.log('[jobService] ✅ Job statistics fetched successfully!');

      return response;
    } catch (error) {
      console.error('[jobService] ❌ Failed to fetch job statistics:', error.message);
      throw error;
    }
  },
};

export default jobService;